import {
  allPermissionModules,
  permissionGroups,
  PermissionAction,
  PermissionActionConfig,
} from "./permissionConfig"

type Role = "super_admin" | "admin" | "operator" | "user" | string | undefined

const moduleActionMap: Record<string, PermissionActionConfig[]> =
  permissionGroups.reduce((acc, group) => {
    group.modules.forEach(module => {
      acc[module.key] = module.actions
    })
    return acc
  }, {} as Record<string, PermissionActionConfig[]>)

const moduleKeyLookup = new Map(
  allPermissionModules.map(key => [key.toLowerCase(), key])
)

const permissionAliasMap: Record<string, string> = {
  "sale-ads": "saleLeads",
  saleads: "saleLeads",
  "solar-lead-management": "solar",
  "solar_lead_management": "solar",
  "solar-leads": "solar",
  "reps & reimbursement": "repReimbursement",
  "reps-reimbursement": "repReimbursement",
  "reps_reimbursement": "repReimbursement",
  "regional-reimbursement": "regionalReimbursement",
  "regional_reimbursement": "regionalReimbursement",
  reps: "reps",
  "recruiting tools": "tools",
  "adminicalendar": "adminifyCalendar",
  "adminify-calendar": "adminifyCalendar",
  "adminify calendar": "adminifyCalendar",
  "dealers social media": "dealerAds",
  "contacts": "conctacts",
  "contact": "conctacts",
  account: "accounts",
  accounts: "accounts",
  "solarlead": "solar",
  "solar-lead": "solar",
  "my-trips": "myTrips",
  "mytrips": "myTrips",
  "trip_details": "tripDetails",
  "trip-detail": "tripDetails",
  tripdetails: "tripDetails",
  "trip-details": "tripDetails",
  "operation-details": "operationDetails",
  operationdetails: "operationDetails",
}

const aliasLookup = new Map(
  Object.entries(permissionAliasMap).map(([alias, value]) => [
    alias.toLowerCase(),
    value,
  ])
)

export const createPermissionKey = (
  moduleKey: string,
  action: PermissionAction = "view"
) => `${moduleKey}:${action}`

export const getModuleActions = (moduleKey: string) =>
  moduleActionMap[moduleKey] ?? []

export const normalizeModuleKey = (raw?: string | null) => {
  if (!raw) return undefined
  const trimmed = raw.trim()
  const lower = trimmed.toLowerCase()
  if (aliasLookup.has(lower)) {
    const aliasTarget = aliasLookup.get(lower)
    const normalized =
      (aliasTarget && moduleKeyLookup.get(aliasTarget.toLowerCase())) ||
      aliasTarget
    return normalized
  }
  return moduleKeyLookup.get(lower) ?? trimmed
}

const expandModuleActions = (moduleKey: string) =>
  (moduleActionMap[moduleKey] ?? []).map(action =>
    createPermissionKey(moduleKey, action.action)
  )

export const expandAllModules = () =>
  Object.keys(moduleActionMap).flatMap(expandModuleActions)

const isKnownAction = (moduleKey: string, action: string): action is PermissionAction =>
  !!moduleActionMap[moduleKey]?.some(item => item.action === action)

const ensureViewSelected = (
  set: Set<string>,
  moduleKey: string,
  action: PermissionAction
) => {
  if (action !== "view") {
    set.add(createPermissionKey(moduleKey, "view"))
  }
}

export const hydratePermissions = (rawPermissions?: string[]): string[] => {
  if (!rawPermissions) return []

  const normalizedPermissions = new Set<string>()
  let includeAll = false

  rawPermissions.forEach(entry => {
    if (!entry) return
    const trimmed = entry.trim()
    if (!trimmed) return

    if (trimmed.toLowerCase() === "all") {
      includeAll = true
      return
    }

    const [rawModule, rawAction] = trimmed.split(":")
    const moduleKey = normalizeModuleKey(rawModule)
    if (!moduleKey) return

    if (!moduleActionMap[moduleKey]) {
      normalizedPermissions.add(trimmed)
      return
    }

    if (rawAction === "*" || rawAction === "all") {
      expandModuleActions(moduleKey).forEach(permission =>
        normalizedPermissions.add(permission)
      )
      return
    }

    const action = rawAction ?? "view"
    const sanitizedAction = isKnownAction(moduleKey, action)
      ? action
      : "view"

    const permissionKey = createPermissionKey(
      moduleKey,
      sanitizedAction as PermissionAction
    )
    normalizedPermissions.add(permissionKey)
    ensureViewSelected(
      normalizedPermissions,
      moduleKey,
      sanitizedAction as PermissionAction
    )
  })

  if (includeAll) {
    expandAllModules().forEach(permission => normalizedPermissions.add(permission))
  }

  return Array.from(normalizedPermissions)
}

export const hasPermission = (
  user:
    | {
        permissions?: string[]
        admin?: Role
        role?: Role
      }
    | undefined,
  moduleKey?: string,
  action: PermissionAction = "view"
) => {
  if (!moduleKey) return true
  if (!user) return false

  const role = (user.admin ?? user.role) as Role
  const normalizedModule = normalizeModuleKey(moduleKey)
  if (!normalizedModule) return false

  if (role === "super_admin") return true

  const permissions = hydratePermissions(user.permissions)
  const requested = createPermissionKey(normalizedModule, action)

  if (permissions.includes(requested)) return true

  /** Vivint routes historically reused other modules or plain tokens (`ads`, `weekly`, `candidates`). */
  if (action === "view") {
    const raw = user.permissions ?? []
    if (normalizedModule === "mediaAds") {
      if (permissions.includes(createPermissionKey("ads", "view"))) return true
      if (Array.isArray(raw) && (raw.includes("ads") || raw.includes("weekly")))
        return true
    }
    if (normalizedModule === "vivintAdsPricing") {
      if (permissions.includes(createPermissionKey("ads", "view"))) return true
      if (Array.isArray(raw) && raw.includes("ads")) return true
    }
    if (normalizedModule === "vivintCandidates") {
      if (permissions.includes(createPermissionKey("candidates", "view")))
        return true
      if (Array.isArray(raw) && raw.includes("candidates")) return true
    }
  }

  // Anyone with Signed Reps (view) can open Employee Roster unless you revoke reps.
  if (
    normalizedModule === "employeeRoster" &&
    action === "view" &&
    permissions.includes(createPermissionKey("reps", "view"))
  ) {
    return true
  }

  // Allow "manage" to satisfy create/edit
  if (
    action !== "view" &&
    permissions.includes(createPermissionKey(normalizedModule, "manage"))
  ) {
    return true
  }

  // When only "view" exists as legacy entry without colon
  if (permissions.includes(normalizedModule)) {
    return true
  }

  return false
}

export const canManageModule = (
  user:
    | {
        permissions?: string[]
        admin?: Role
        role?: Role
      }
    | undefined,
  moduleKey: string
) =>
  ["create", "edit", "delete"].some(action =>
    hasPermission(user, moduleKey, action as PermissionAction)
  )

export const canDeleteModule = (
  user:
    | {
        permissions?: string[]
        admin?: Role
        role?: Role
      }
    | undefined,
  moduleKey: string
) => hasPermission(user, moduleKey, "delete")

export const canCreateModule = (
  user:
    | {
        permissions?: string[]
        admin?: Role
        role?: Role
      }
    | undefined,
  moduleKey: string
) => hasPermission(user, moduleKey, "create")

export const canEditModule = (
  user:
    | {
        permissions?: string[]
        admin?: Role
        role?: Role
      }
    | undefined,
  moduleKey: string
) => hasPermission(user, moduleKey, "edit")

export const serializePermissions = (permissions: string[]) => {
  const normalized = new Set<string>()

  permissions.forEach(permission => {
    if (permission) {
      normalized.add(permission)
      const [rawModule] = permission.split(":")
      const moduleKey = normalizeModuleKey(rawModule)
      if (moduleKey && moduleActionMap[moduleKey]) {
        normalized.add(moduleKey)
      }
    }
  })

  return Array.from(normalized)
}

